import { i18n } from '@/shared/i18n';
import { CONNECTOR_PLATFORM_LABELS } from '@/entities/media-connector';
import { planBlocker, type JobPlan, type PlanBlocker } from '@/entities/media-campaign';
import { type PreflightStatus } from './preflight';

export type GateTone = 'muted' | 'bad' | 'good';

export interface PublishGate {
  tone: GateTone;
  /** Short enough for the header chip. */
  label: string;
  /** The whole sentence, for the tooltip that explains a disabled Publish. */
  detail: string;
  passed: boolean;
}

/** Names the platforms at fault: the review shows one at a time, so "a platform
 *  has a problem" would send the reviewer hunting through tabs. */
function describeBlocker(blocker: PlanBlocker): string {
  if (blocker.kind === 'no_changes')
    return i18n.t('projects:deliveryJobReview.publishGate.detail.noChanges');

  const where = blocker.platforms
    .map((platform) => CONNECTOR_PLATFORM_LABELS[platform])
    .join(i18n.t('projects:deliveryJobReview.publishGate.platformJoin'));

  if (blocker.kind === 'clients_needed')
    return i18n.t('projects:deliveryJobReview.publishGate.detail.clientsNeeded', { where });
  return blocker.kind === 'unbound_accounts'
    ? i18n.t('projects:deliveryJobReview.publishGate.detail.unboundAccounts', { where })
    : i18n.t('projects:deliveryJobReview.publishGate.detail.fieldsNeedFixing', {
        count: blocker.count,
        where,
      });
}

/**
 * The platforms in this plan that cannot be asked about it before it is written.
 *
 * Only an explicit false counts: a plan fetched before the field existed says nothing,
 * because claiming a working connection cannot be checked is the worse mistake.
 */
function uncheckablePlatforms(plan: JobPlan): string {
  return plan.groups
    .filter((group) => group.dryRunSupported === false)
    .map((group) => CONNECTOR_PLATFORM_LABELS[group.platform])
    .join(i18n.t('projects:deliveryJobReview.publishGate.platformJoin'));
}

/** The chip has room for a few words, so it says what is wrong and leaves where to the tooltip. */
function labelBlocker(blocker: PlanBlocker): string {
  if (blocker.kind === 'no_changes')
    return i18n.t('projects:deliveryJobReview.publishGate.label.nothingToPublish');
  if (blocker.kind === 'unbound_accounts')
    return i18n.t('projects:deliveryJobReview.publishGate.label.noAdAccount');
  if (blocker.kind === 'clients_needed')
    return i18n.t('projects:deliveryJobReview.publishGate.label.noClient');
  return i18n.t('projects:deliveryJobReview.publishGate.label.toFix', { count: blocker.count });
}

/**
 * Why Publish is or is not available, in one place.
 *
 * The header shows `label` and the disabled Publish repeats `detail` on hover: a
 * greyed-out button with no stated reason is the one thing this screen cannot
 * afford, since fixing the plan is the whole job.
 */
export function publishGate(
  plan: JobPlan,
  status: PreflightStatus,
  isPublished = false,
): PublishGate {
  const blocker = planBlocker(plan);
  const passed = status === 'checked' && blocker === null;

  /**
   * A published job with nothing left to send.
   *
   * `planBlocker` decides that, not the publish date. A job used to be finished the
   * moment it went out, which stopped being true when a published plan became editable:
   * fixing a headline afterwards left Publish greyed out with pre-flight reporting
   * nothing to fix and no way to send the fix.
   */
  if (isPublished && blocker?.kind === 'no_changes') {
    return {
      tone: 'muted',
      label: i18n.t('projects:deliveryJobReview.publishGate.label.published'),
      detail: i18n.t('projects:deliveryJobReview.publishGate.detail.published'),
      passed: false,
    };
  }

  if (status === 'running') {
    return {
      tone: 'muted',
      label: i18n.t('projects:deliveryJobReview.publishGate.label.checking'),
      detail: i18n.t('projects:deliveryJobReview.publishGate.detail.checking'),
      passed,
    };
  }

  if (status === 'idle') {
    return {
      tone: 'muted',
      label: i18n.t('projects:deliveryJobReview.publishGate.label.preflightNotRun'),
      detail: i18n.t('projects:deliveryJobReview.publishGate.detail.preflightNotRun'),
      passed,
    };
  }

  if (status === 'stale') {
    return {
      tone: 'muted',
      label: i18n.t('projects:deliveryJobReview.publishGate.label.recheckNeeded'),
      detail: i18n.t('projects:deliveryJobReview.publishGate.detail.recheckNeeded'),
      passed,
    };
  }

  if (blocker)
    return { tone: 'bad', label: labelBlocker(blocker), detail: describeBlocker(blocker), passed };

  /**
   * Still `good`, and Publish stays available: our own rules did pass, and on a platform
   * with no validate-only surface there is nothing further to wait for.
   *
   * What changes is the sentence. "All checks passed" over a plan the platform was never
   * shown is the claim this screen used to make, and it is the one that let a DV360 plan go
   * out reading as fully verified.
   */
  const unchecked = uncheckablePlatforms(plan);

  return {
    tone: 'good',
    label: i18n.t('projects:deliveryJobReview.publishGate.label.checksPassed'),
    detail: unchecked
      ? i18n.t('projects:deliveryJobReview.publishGate.detail.checksPassedUnchecked', {
          where: unchecked,
        })
      : i18n.t('projects:deliveryJobReview.publishGate.detail.checksPassed'),
    passed,
  };
}
